import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, createStaticVNode as _createStaticVNode, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-09e82c56"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "logoCt" }
const _hoisted_2 = ["src"]
const _hoisted_3 = /*#__PURE__*/_createStaticVNode("<div class=\"attribution\" data-v-09e82c56><a href=\"https://www.mapbox.com/about/maps/\" data-v-09e82c56>©<span class=\"hover:underline\" data-v-09e82c56>Mapbox</span></a><a href=\"http://www.openstreetmap.org/copyright\" data-v-09e82c56>©<span class=\"hover:underline\" data-v-09e82c56>OpenStreetMap</span></a><a href=\"https://www.mapbox.com/map-feedback/\" target=\"_blank\" data-v-09e82c56><span class=\"hover:underline\" data-v-09e82c56>Improve this map</span></a></div>", 1)
const _hoisted_4 = {
  key: 6,
  class: "startHint"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 1,
  class: "flex gap-x-1.5 items-center"
}
const _hoisted_7 = { class: "flex align-center gap-x-1.5" }

import Map from '@/components/Map.vue';
import LogoSvg from '@/assets/logo.svg';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import DrawToolCard from '@/components/DrawToolCard.vue';
import LayerToggleCard from '@/components/LayerToggleCard.vue';
import Searchbar from '@/components/Searchbar.vue';
import FeatureCard from '@/components/FeatureCard.vue';
import { distanceBtwCoordiantes } from '@/utils/geo-methods';
import {
  getAdvisoryByCircle,
  getAdvisoryByPoint,
  getAdvisoryByPolygon,
  getAdvisoryBySquare,
} from '@/services/utm-services';
// import { reduce } from 'lodash';
import reduce from 'lodash/reduce';
import { layerDescription } from '@/utils/layer';
import WeatherCard from '@/components/WeatherCard.vue';
import {
  showResults as showSearchResults,
  weatherLocation,
  weather,
  currLoc,
  showWeatherForCoordinate,
  featureLoading,
  featureCardData,
} from '@/states/homeState';
import {
  EventData,
  LngLatBounds,
  LngLatLike,
  MapMouseEvent,
  MapTouchEvent,
  Marker,
} from 'mapbox-gl';
import polygonCenterOfMass from '@turf/center-of-mass';
import { polygon, point } from '@turf/helpers';
import { AdvisoryResults } from '@/typings/map';
import { useRoute } from 'vue-router';
import { isEmpty } from 'lodash';
import circle from '@turf/circle';
import { Feature } from 'geojson';
import bbox from '@turf/bbox';


export default /*#__PURE__*/_defineComponent({
  __name: 'Home',
  setup(__props) {

const mapRef = ref<InstanceType<typeof Map>>();
const drawRef = ref<InstanceType<typeof DrawToolCard>>();
const marker = ref<Marker>();

const showStartHint = ref<'zoomin' | 'draw'>('draw');
const showLayersCard = ref(false);
const showFeatureCard = ref(false);

const showSuccessCopySnackbar = ref(false);
const route = useRoute();
const onLayerToggled = () => {
  showLayersCard.value = !showLayersCard.value;
  showFeatureCard.value = false;
  showSearchResults.value = false;
};

const toggleFeatureCard = (feature) => {
  showLayersCard.value = false;
  showSearchResults.value = false;
  featureCardData.value = feature;
  showFeatureCard.value = true;
};

const closeFeatureCard = () => {
  featureCardData.value = null;
  showFeatureCard.value = false;

  // removing marker
  marker.value.remove();
  marker.value = null;

  // remove drawn shape
  drawRef.value.deleteSelectedDrawing();
};

const copyCoordinates = async () => {
  await navigator.clipboard.writeText(currLoc.value);
  showSuccessCopySnackbar.value = true;
};

const getAdvisoryForDrawing = async ({ shape, feature }) => {
  let res, lng: number, lat: number;
  showFeatureCard.value = true;
  featureLoading.value = true;

  switch (shape) {
    case 'rectangle':
      const square = (feature.geometry as any).coordinates[0];

      // getting the center coordinates of the shape
      ({ lng, lat } = new LngLatBounds(
        [square[0][0], square[0][1]],
        [square[2][0], square[2][1]]
      ).getCenter());

      setWeatherLocation(lat, lng);

      // moving marker to center of rectangle
      setMarker({ lng, lat });

      res = await getAdvisoryBySquare(square[0][1], square[0][0], square[2][1], square[2][0]);
      break;

    case 'polygon':
      const polyCoords = (feature.geometry as any).coordinates[0] as number[][];

      [lng, lat] = polygonCenterOfMass(polygon([polyCoords])).geometry.coordinates;

      setWeatherLocation(lat, lng);

      // moving marker to center of the polygon
      setMarker({ lng, lat });

      res = await getAdvisoryByPolygon(polyCoords);
      break;

    case 'circle':
      [lng, lat] = (feature.properties as any).center;

      setWeatherLocation(lat, lng);

      // moving marker to center of the circle
      setMarker({ lat, lng });

      const circleRadius = distanceBtwCoordiantes(
        (feature.properties as any).radiusInKm,
        'kilometers'
      );

      res = await getAdvisoryByCircle(lat, lng, circleRadius);
      break;

    case 'point':
      [lat, lng] = (feature.geometry as any).coordinates;

      // NOTE: these are already set in this call chain getInformationForCoord -> onMapClicked
      // setWeatherLocation(lat, lng);
      // moving marker to center of the circle
      // setMarker({ lat, lng });

      res = await getAdvisoryByPoint(lat, lng);
      break;
  }

  if (res.data.success) {
    const processedAdvisories: AdvisoryResults['advisory'] = reduce(
      res.data.data,
      (p, layerProperties, layerId) => {
        // if we have description details about layer id
        // it has some features add them to advisory results
        if (layerId in layerDescription && layerProperties.length > 0) {
          const criticality = layerDescription[layerId]?.criticality;

          // criticality property not exist
          if (!p[criticality]) {
            p[criticality] = {};
          }

          // source layer key not exist
          if (!(layerId in p[criticality])) {
            p[criticality][layerId] = {
              layer_key: layerId,
              layer_title: layerDescription[layerId]?.label,
              metadata: {
                layerId: layerDescription[layerId]?.groupLayerId,
                criticality: layerDescription[layerId]?.criticality,
              },
              data: [],
            };
          }

          // insert feature properties to its respecitve grouping
          p[criticality][layerId].data = layerProperties;
        }

        return p;
      },
      {
        Prohibited: null,
        'Caution - Authorization Required': null,
        'Caution - Information': null,
        Free: null,
      }
    );

    // change marker color based on the zone status type
    const markerElement = marker.value.getElement().querySelector('g:nth-child(2)') as SVGGElement;
    switch (res.data.zone) {
      case 'red':
        markerElement.style.fill = '#e63946';
        break;
      case 'yellow':
        markerElement.style.fill = '#ffb703';
        break;
      case 'green':
      case 'greenish':
        markerElement.style.fill = 'green';
        break;
      default:
        markerElement.style.fill = null;
    }

    toggleFeatureCard({
      advisory: processedAdvisories,
      ...('zone' in res.data && { zone: res.data.zone }),
    });
    featureLoading.value = false;
  }
};

const toggleStartHint = () => {
  if (mapRef.value.map.getZoom() < 9 && showStartHint.value !== 'zoomin') {
    showStartHint.value = 'zoomin';
  } else if (mapRef.value.map.getZoom() >= 9 && showStartHint.value !== 'draw') {
    showStartHint.value = 'draw';
  }
};

const setWeatherLocation = (lat: number, lng: number) => {
  showWeatherForCoordinate({ lat, lng });
};

const setMarker = (position: LngLatLike) => {
  if (!marker.value) {
    marker.value = new Marker({ color: '#f60' }).setLngLat(position).addTo(mapRef.value.map);
  } else {
    // updating existing marker coordinates
    marker.value.setLngLat(position);
  }
};

const getInformationForCoord = async (coord: { lng: number; lat: number }) => {
  // checking if map is not in drawing state
  if (
    !mapRef.value.map.isMoving() &&
    !drawRef.value.isDrawing &&
    drawRef.value.draw.getSelectedIds().length === 0
  ) {
    // showing marker on the map where user clicked
    setMarker(coord);
    setWeatherLocation(coord.lat, coord.lng);
    getAdvisoryForDrawing({ shape: 'point', feature: point([coord.lat, coord.lng]) });
  }
};

const onMapClicked = (e: (MapMouseEvent | MapTouchEvent) & EventData) => {
  getInformationForCoord(e.lngLat);
};

watch(drawRef, async (drawNew) => {
  if (drawNew) {
    if (!isEmpty(route.query) && route.query.share) {
      const sharedFeature = JSON.parse(atob(route.query.share as string));

      if (sharedFeature.shape == 'point') {
        // fetch advisory for the point
        await getInformationForCoord({ lat: sharedFeature.coord[0], lng: sharedFeature.coord[1] });
        // center map on to the point
        mapRef.value.map.jumpTo({ center: sharedFeature.coord.reverse() });
      } else {
        let feature: Feature;
        switch (sharedFeature.shape) {
          case 'circle':
            feature = circle(sharedFeature.center, sharedFeature.radius, {
              properties: {
                isCircle: true,
                center: sharedFeature.center,
                radiusInKm: sharedFeature.radius,
                shape: 'circle',
              },
            });
            break;
          case 'polygon':
            feature = polygon(sharedFeature.polygon, {
              properties: { shape: 'polygon' },
            });
            break;
        }
        // add polygon on the map
        const addedFeature = drawRef.value.draw.add(feature);
        // select added polygon
        drawRef.value.draw.changeMode('simple_select', { featureIds: addedFeature });
        // zoom map to fit polygon into view
        mapRef.value.map.fitBounds(bbox(feature) as any, { padding: 200 });
        // fetch advisories for the polygon
        await getAdvisoryForDrawing({ shape: sharedFeature.shape, feature });
      }
    }
  }
});

onMounted(() => {
  // ask for location permission
  // if open via shared link ignore
  if (isEmpty(route.query)) {
    (navigator?.geolocation as Geolocation)?.getCurrentPosition?.((currGeoLoc) => {
      mapRef.value.map.setCenter({
        lat: currGeoLoc.coords.latitude,
        lng: currGeoLoc.coords.longitude,
      });
      getInformationForCoord({
        lat: currGeoLoc.coords.latitude,
        lng: currGeoLoc.coords.longitude,
      });
    });
  }

  // checking the zoom level and hiding/showing start hint text based on it
  toggleStartHint();
  mapRef.value.map.on('zoomend', toggleStartHint);

  // setting location coordinates at the time of mounting
  const { lat, lng } = mapRef.value.map.getCenter();
  setWeatherLocation(lat, lng);

  // every time map moves updating weather location
  // mapRef.value.map.on('moveend', (e) => {
  //   const { lat, lng } = e.target.getCenter();
  //   setWeatherLocation(lat, lng);
  // });

  // updating weather location based on user click location
  mapRef.value.map.on('click', onMapClicked);
  mapRef.value.map.on('touchend', onMapClicked);
});

onUnmounted(() => {
  mapRef.value.map.off('zoomend', toggleStartHint);
  mapRef.value.map.off('click', onMapClicked);
});

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_VSnackbar = _resolveComponent("VSnackbar")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Map, {
      ref_key: "mapRef",
      ref: mapRef
    }, null, 512),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("img", {
        src: _unref(LogoSvg),
        alt: "Watchtower by skylark drones"
      }, null, 8, _hoisted_2)
    ]),
    (mapRef.value)
      ? (_openBlock(), _createBlock(Searchbar, {
          key: 0,
          map: mapRef.value.map,
          "go-to-coordinate": getInformationForCoord,
          class: "search_bar"
        }, null, 8, ["map"]))
      : _createCommentVNode("", true),
    (showLayersCard.value)
      ? (_openBlock(), _createBlock(LayerToggleCard, {
          key: 1,
          class: "layerToggleCard",
          onOnClose: onLayerToggled
        }))
      : _createCommentVNode("", true),
    (showFeatureCard.value)
      ? (_openBlock(), _createBlock(FeatureCard, {
          key: 2,
          class: "featureCard",
          featureData: _unref(featureCardData),
          featureLoading: _unref(featureLoading),
          draw: drawRef.value.draw,
          map: mapRef.value.map,
          onOnClose: closeFeatureCard
        }, null, 8, ["featureData", "featureLoading", "draw", "map"]))
      : _createCommentVNode("", true),
    (mapRef.value)
      ? (_openBlock(), _createBlock(DrawToolCard, {
          key: 3,
          ref_key: "drawRef",
          ref: drawRef,
          onOnSelectionChange: getAdvisoryForDrawing,
          onOnDrawCreate: getAdvisoryForDrawing,
          onOnDrawUpdate: getAdvisoryForDrawing,
          onOnLayerToggle: onLayerToggled,
          map: mapRef.value.map
        }, null, 8, ["map"]))
      : _createCommentVNode("", true),
    (_unref(currLoc) && showFeatureCard.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 4,
          class: "currLoc",
          onClick: copyCoordinates
        }, [
          _createVNode(_component_Icon, { icon: "mdi:map-marker-outline" }),
          _createTextVNode(_toDisplayString(_unref(currLoc)), 1)
        ]))
      : _createCommentVNode("", true),
    (_unref(weatherLocation))
      ? (_openBlock(), _createBlock(WeatherCard, {
          key: 5,
          class: "weather",
          weather: _unref(weather),
          location: _unref(weatherLocation)
        }, null, 8, ["weather", "location"]))
      : _createCommentVNode("", true),
    _hoisted_3,
    (showStartHint.value)
      ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
          (showStartHint.value === 'draw')
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, "Click on the map or use draw tools to get advisory details"))
            : _createCommentVNode("", true),
          (showStartHint.value === 'zoomin')
            ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                _createVNode(_component_Icon, {
                  icon: "mdi:magnify-plus-outline",
                  class: "text-xl"
                }),
                _createTextVNode(" Zoom in to the see advisory layers ")
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showStartHint.value = null)),
            class: "close-btn"
          }, [
            _createVNode(_component_Icon, { icon: "mdi:close" })
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_VSnackbar, {
      location: "top center",
      class: "sucess",
      modelValue: showSuccessCopySnackbar.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showSuccessCopySnackbar).value = $event))
    }, {
      actions: _withCtx(() => [
        _createElementVNode("button", {
          class: "v-snackbar-close-btn",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (showSuccessCopySnackbar.value = false))
        }, [
          _createVNode(_component_Icon, { icon: "mdi:close" })
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("span", _hoisted_7, [
          _createVNode(_component_Icon, {
            icon: "mdi:check",
            class: "inline"
          }),
          _createTextVNode(" Coordinates copied to clipboard ")
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})